import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { ChatMessage } from '../models/chat-message.model';
import { ChatConfigService } from './chat-config.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { catchError, first } from 'rxjs/operators';
import { TokenHelperService } from 'gematik-form-library';

@Injectable({ providedIn: 'root' })
export class ChatService {
  public readonly messages$: Observable<ChatMessage[]>;
  public readonly messageError$: Observable<string>;

  private readonly messagesSubject = new BehaviorSubject<ChatMessage[]>([]);
  private readonly messageErrorSubject = new Subject<string>();
  private conversationId: string | null = null;

  private readonly config = inject(ChatConfigService).getConfig();
  private readonly translateService = inject(TranslateService);
  private readonly tokenHelperService = inject(TokenHelperService);

  constructor(private readonly http: HttpClient) {
    this.messages$ = this.messagesSubject.asObservable();
    this.messageError$ = this.messageErrorSubject.asObservable();
  }

  sendMessage(content: string) {
    if (!content.trim()) return;
    const trimmed = content.trim();

    const user: ChatMessage = {
      id: this.uuid(),
      role: 'user',
      content: trimmed,
      createdAt: new Date(),
    };
    this.append(user);

    const assistant: ChatMessage = {
      id: this.uuid(),
      role: 'assistant',
      content: '...',
      createdAt: new Date(),
      pending: true,
    };
    this.append(assistant);

    const payload: { question: string; conv?: string } = {
      question: trimmed,
    };

    if (this.conversationId) {
      payload.conv = this.conversationId;
    }

    const url = `${this.config.apiBaseUrl}/askQuestion`;

    const headers = this.buildAuthHeaders();

    this.http
      .post<{ answer: string; conv: string }>(url, payload, { headers })
      .pipe(
        first(),
        catchError(error => {
          console.error('Error calling /askQuestion', error);
          this.update(assistant.id, {
            content: this.translateService.instant('chatbot.chat.error.genericMessage'),
            pending: false,
            error: true,
          });
          this.messageErrorSubject.next(trimmed);
          return of(null);
        }),
      )
      .subscribe(response => {
        if (!response) {
          return;
        }

        if (response.conv) {
          this.conversationId = response.conv;
        }

        this.update(assistant.id, {
          content: response.answer,
          pending: false,
          error: false,
        });
      });
  }

  resetConversation() {
    this.messagesSubject.next([]);
    this.conversationId = null;
  }

  private append(m: ChatMessage) {
    this.messagesSubject.next([...this.messagesSubject.value, m]);
  }

  private update(id: string, patch: Partial<ChatMessage>) {
    this.messagesSubject.next(this.messagesSubject.value.map(m => (m.id === id ? { ...m, ...patch } : m)));
  }

  private uuid(): string {
    return Math.random().toString(36).substring(2, 10);
  }

  /**
   * Builds HTTP headers with Authorization token if available.
   * If no token exists, returns empty headers.
   */
  private buildAuthHeaders(): HttpHeaders {
    let headers = new HttpHeaders();

    try {
      const token = this.tokenHelperService.getToken();
      if (token && !this.tokenHelperService.isExpired()) {
        headers = headers.set('Authorization', `Bearer ${token}`);
      }
    } catch {
      // No token available - continue without auth header
    }

    return headers;
  }
}
